import { gsap } from "gsap";
import { Container, Sprite } from "pixi.js";

import { AbstractSpine } from "../../../../abstracts/graphics/AbstractSpine";
import { shuffleArray } from "../../../../abstracts/utils/ArrayUtils";
import { generateScaledSprite } from "../../../../abstracts/utils/GraphicUtils";
import { getRandomBetween } from "../../../../abstracts/utils/JSUtils";
import { playSound } from "../../../../abstracts/utils/SoundUtils";
import { symbolConfig } from "../../../UCConfig";
import { UCSoundNames, UCSpineNames } from "../../../assetsLoader/UCAssetsNames";
import { UCSpecialSymbolNames, UCSymbolNames, UCSymbolSize } from "../../../types/UCEnums";
import { fishLandSounds } from "../../Sound/UCSoundController";
import { UCSymbol } from "./UCSymbol";
import { symbolsResizeConfig } from "./UCSymbolsResizeConfig";

export class UCBigFishSymbol extends UCSymbol {
    protected slotContainer: Container;
    protected fishesContainer: Container;
    protected fishesContainerMask: Sprite;
    protected fishElements: Container[] = [];
    protected fishPositions: number[];

    protected elementDefaultHeight: number;
    protected fishScaleFactor: number;

    constructor() {
        super(UCSymbolNames.BIG_FISH);
    }

    protected init(): void {
        super.init();

        this.fishElements = [];
        this.slotContainer = new Container();
        this.slotContainer.name = `SlotContainer`;

        const woodBoard = generateScaledSprite(`BF_sign`);
        woodBoard.name = `woodBoard`;
        this.slotContainer.addChild(woodBoard);

        this.fishesContainer = new Container();
        this.fishesContainer.name = `FishesContainer`;
        this.slotContainer.addChild(this.fishesContainer);

        this.fishesContainerMask = generateScaledSprite(`BF_mask`);
        this.fishesContainerMask.name = `fishesContainerMask`;
        this.slotContainer.addChild(this.fishesContainerMask);
        this.fishesContainer.mask = this.fishesContainerMask;

        this.fishPositions = [];
        this.elementDefaultHeight = 102;
        this.fishScaleFactor = 0.77;

        for (let i = 0; i < 20; i++) {
            this.fishPositions.push(-i * this.elementDefaultHeight);
        }
    }

    protected generateSymbol(): void {
        this.mainSpine = new AbstractSpine(UCSpineNames.BIG_FISH);

        this.mainSpine.x =
            symbolConfig.symbolWidth / 2 +
            symbolsResizeConfig.get(this.name as UCSymbolNames)!.default.offsetX!;
        this.mainSpine.y =
            symbolConfig.symbolHeight / 2 +
            symbolsResizeConfig.get(this.name as UCSymbolNames)!.default.offsetY!;
        this.addChild(this.mainSpine);
    }

    public revealFish(fishSymbolName: UCSpecialSymbolNames): gsap.core.Timeline | gsap.core.Tween {
        this.revealedSymbolName = fishSymbolName;

        let fishCounter = 0;
        const tl = gsap.timeline();

        const bigFishBoardActivation = this.mainSpine.getSpineTween(
            `Win`,
            false,
            () => {
                playSound({ name: UCSoundNames.BIG_FISH_SPIN });
            },
            null,
            () => {
                this.slotContainer.alpha = 0;
                this.slotContainer.scale.set(0.98);

                this.slotContainer.position.set(0, 3);

                if (this.size === UCSymbolSize.X033) {
                    this.slotContainer.scale.set(0.8);
                    this.slotContainer.position.set(13, -34);
                } else if (this.size === UCSymbolSize.X075) {
                    // this.slotContainer.scale.set(0.9);
                    this.slotContainer.position.set(1, -2);
                } else if (this.size === UCSymbolSize.X1) {
                    this.slotContainer.position.set(1, 10);
                }

                this.slotContainer.rotation = -0.015;
                this.addChild(this.slotContainer);
            }
        );

        tl.add(bigFishBoardActivation);

        if (this.size === UCSymbolSize.X1) {
            const centerSymbolTween = gsap.to(this.mainSpine, {
                pixi: { y: `+=8` },
                duration: bigFishBoardActivation.duration(),
            });

            tl.add(centerSymbolTween, `<`);
        }

        const showSlotContainerTween = gsap.to(this.slotContainer, {
            alpha: 1,
            duration: 0.1,
        });
        tl.add(showSlotContainerTween);

        const hideMainSpine = gsap.to(this.mainSpine, {
            alpha: 0,
            duration: 0.1,
            onComplete: () => {
                this.removeChild(this.mainSpine);
                this.mainSpine.getSpineTween(`Normal`);
            },
        });
        tl.add(hideMainSpine, `<`);

        const generateFish = (
            fishName: UCSymbolNames,
            addQuestionMark: boolean = true,
            isWinningFish: boolean = false
        ): Container => {
            const fishContainer = new Container();
            fishContainer.name = `fishContainer`;
            const fishGraphics = generateScaledSprite(fishName);
            fishContainer.addChild(fishGraphics);

            if (addQuestionMark) {
                const questionMark = generateScaledSprite(`BF/BF_question_alpha`);
                questionMark.scale.set(1.2);
                questionMark.position.set(19, 14);
                fishContainer.addChild(questionMark);
            }

            fishContainer.y = this.fishPositions[fishCounter];
            fishCounter++;

            this.fishesContainer.addChild(fishContainer);

            if (isWinningFish) {
                fishContainer.name = `winningFish`;
            }

            return fishContainer;
        };

        for (let i = 0; i < 4; i++) {
            for (let j = 0; j < 4; j++) {
                const fishElement = generateFish(`F${i + 4}` as UCSymbolNames);
                this.fishElements.push(fishElement);
            }
        }

        shuffleArray(this.fishElements);

        this.fishElements.forEach((element, index) => {
            element.x = 0;
            element.getChildAt(0).alpha = index <= 1 ? 0 : index * (1 / this.fishElements.length);
            element.getChildAt(1).alpha =
                index <= 1 ? 1 : 1 - index * (1 / this.fishElements.length);
            element.y = -(index + 1) * this.elementDefaultHeight;
        });

        generateFish(fishSymbolName, false, true);

        generateFish(`F${getRandomBetween(4, 7, true)}` as UCSymbolNames, false);

        const fishesContainerInitialHeight = this.fishesContainer.height;
        this.fishesContainer.scale.set(0.77);
        this.fishesContainer.x = 22;
        this.fishesContainer.y = this.elementDefaultHeight * this.fishScaleFactor + 2;

        const slotAnimation = gsap.to(this.fishesContainer, {
            y: this.fishesContainer.height - this.elementDefaultHeight * this.fishScaleFactor,
            ease: `back.inOut(1.2)`,
            duration: 4,
            delay: 0.5,
            onComplete: () => {
                const fishId: number = +fishSymbolName.replace(/^\D+/g, ``);

                playSound({ name: fishLandSounds[fishId - 1] });
            },
        });
        tl.add(slotAnimation);

        let newXPosition = 50;
        let newYPosition = -3;
        let newScale = 0.53;
        if (this.size === UCSymbolSize.X033) {
            newXPosition = 54;
            newYPosition = -11;
            newScale = 0.38;
        } else if (this.size === UCSymbolSize.X075) {
            newXPosition = 52;
            newYPosition = -5;
        }

        const moveSlotBackAnimation = gsap.to(this.slotContainer, {
            x: newXPosition,
            y: newYPosition,
            ease: `power1.out`,
            rotation: -0.035,
            duration: 0.3,
            delay: 0.3,
            pixi: { scale: newScale },
            onStart: () => {
                if (this.size === UCSymbolSize.X1) {
                    this.mainSpine.y -= 8;
                }
            },
        });
        tl.add(moveSlotBackAnimation);

        const scaleFishContainerAnimation = gsap.to(this.fishesContainer, {
            x: -34,
            y: (fishesContainerInitialHeight - this.elementDefaultHeight) * 1.7 - 96,
            pixi: { scale: 1.7 },
            duration: 0.5,
        });
        tl.add(scaleFishContainerAnimation, `<`);

        const showMainSpine = gsap.to(this.mainSpine, {
            alpha: 1,
            duration: 0.3,
            onStart: () => {
                this.mainSpine.alpha = 0;
                this.addChildAt(this.mainSpine, 0);
            },
        });
        tl.add(showMainSpine, `<`);

        return tl;
    }

    public override getWinAnimation(): gsap.core.Tween | gsap.core.Timeline {
        const tl = super.getWinAnimation();

        const winningFish = (
            this.fishesContainer.getChildByName(`winningFish`) as Container
        )?.getChildAt(0);

        if (winningFish) {
            const slotAnimation = gsap.to(winningFish, {
                pixi: { tint: 0xffffff },
                duration: 0.1,
            });

            (tl as gsap.core.Timeline).add(slotAnimation, 0);
        }

        return tl;
    }

    public getNoWinAnimation(): gsap.core.Tween | gsap.core.Timeline {
        const tl = gsap.timeline();

        const symbolAnimation = gsap.to(this.mainSpine, {
            pixi: { tint: 0x555555 },
            duration: 0.1,
        });

        tl.add(symbolAnimation, 0);

        const winningFish = (
            this.fishesContainer.getChildByName(`winningFish`) as Container
        )?.getChildAt(0);

        if (winningFish) {
            const slotAnimation = gsap.to(winningFish, {
                pixi: { tint: 0x555555 },
                duration: 0.1,
            });

            tl.add(slotAnimation, 0);
        }

        return tl;
    }

    protected override resetMainSpine(isSpin: boolean = false): void {
        if (this.mainSpine !== undefined) {
            this.mainSpine.reset();

            if (!isSpin) {
                this.mainSpine.mask = null;
                this.mainSpine.scale.set(0.5);

                this.mainSpine.x =
                    symbolConfig.symbolWidth / 2 +
                    symbolsResizeConfig.get(this.name as UCSymbolNames)!.default.offsetX!;
                this.mainSpine.y =
                    symbolConfig.symbolHeight / 2 +
                    symbolsResizeConfig.get(this.name as UCSymbolNames)!.default.offsetY!;
            }

            this.addChildAt(this.mainSpine, 0);
        }
    }

    override reset() {
        super.reset();

        this.removeChild(this.slotContainer);
        this.fishesContainer.removeChildren();
        this.fishesContainer.scale.set(1);
        this.fishElements = [];
        this.revealedSymbolName = ``;
    }
}
